import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { AdminIdentity, AdminUser, Role } from "../types";

/**
 * Every control here is mirrored by a server-side guard (self-mutation, last
 * owner, allowed domain). Disabling a button is a courtesy, not a defence — the
 * API refuses these regardless of what the UI renders.
 */
export function Admins({ me }: { me: AdminIdentity }) {
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.listAdmins();
      setRows(res.items);
      setDomain(res.allowed_domain);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    void run("add", async () => {
      await api.addAdmin(email.trim(), role);
      setEmail("");
      setRole("member");
    });
  };

  return (
    <section>
      <header className="section-head">
        <h2>Admins</h2>
        <button className="ghost" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      {!me.is_owner && (
        <p className="muted">
          You have member access — you can review the waitlist, but only an owner
          can change who has admin access.
        </p>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {me.is_owner && (
        <form className="add-admin" onSubmit={add}>
          <input
            type="email"
            placeholder={domain ? `someone@${domain}` : "someone@example.com"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
          />
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="member">member</option>
            <option value="owner">owner</option>
          </select>
          <button className="primary" disabled={busy === "add" || !email.trim()}>
            {busy === "add" ? "Adding…" : "Grant access"}
          </button>
        </form>
      )}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Granted</th>
              {me.is_owner && <th className="right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const isSelf = a.email === me.email;
              const k = (op: string) => `${op}:${a.email}`;
              return (
                <tr key={a.email} className={a.disabled ? "dim" : undefined}>
                  <td>
                    {a.email}
                    {isSelf && <span className="badge"> you</span>}
                  </td>
                  <td>{a.role}</td>
                  <td>{a.disabled ? "disabled" : "active"}</td>
                  <td className="nowrap muted">{a.added_by || "—"}</td>
                  {me.is_owner && (
                    <td className="right nowrap">
                      {isSelf ? (
                        <span className="muted small">ask another owner</span>
                      ) : (
                        <>
                          <button
                            className="ghost small"
                            disabled={!!busy}
                            onClick={() =>
                              void run(k("role"), () =>
                                api.patchAdmin(a.email, {
                                  role: a.role === "owner" ? "member" : "owner",
                                }),
                              )
                            }
                          >
                            {a.role === "owner" ? "Make member" : "Make owner"}
                          </button>
                          <button
                            className="ghost small"
                            disabled={!!busy}
                            onClick={() =>
                              void run(k("dis"), () =>
                                api.patchAdmin(a.email, { disabled: !a.disabled }),
                              )
                            }
                          >
                            {a.disabled ? "Enable" : "Disable"}
                          </button>
                          <button
                            className="danger small"
                            disabled={!!busy}
                            onClick={() => {
                              if (!confirm(`Permanently remove ${a.email}?`)) return;
                              void run(k("del"), () => api.deleteAdmin(a.email));
                            }}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="muted small">
        Disabling revokes access but keeps the record of who granted it and who
        revoked it. Removing deletes the record entirely.
      </p>
    </section>
  );
}
